import React, { useState, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import RecommendationSection from './sections/RecommendationSection';
import LiveSection from './sections/LiveSection';
import ListSection from './sections/ListSection';
import Footer from '../../components/Footer';
import DataLoader from '../../components/DataLoader';
import LoginRequiredModal from '../../components/LoginRequiredModal';
import { useUserAuth } from '../../context/UserAuthContext';
import { useFavoriteManager } from '../../hooks/useFavoriteManager';
import { useDevice } from '../../context/DeviceContext';
import useAppleCallback from '../../hooks/useAppleCallback';

const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* max-width: 100%; */
  /* overflow-x: hidden; */
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* max-width: 100%; */
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px); /* 앱바 높이를 제외한 전체 화면 높이 */
  width: 100%;
`;

const MobileSpacer = styled.div`
  height:30px;
  width: 100%;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

function Home() {
  // Apple 로그인 콜백 처리
  useAppleCallback();
  
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const favoriteManager = useFavoriteManager();
  const device = useDevice();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // 각 섹션의 로딩 상태 관리
  const [recommendationLoading, setRecommendationLoading] = useState(true);
  const [liveLoading, setLiveLoading] = useState(device === 'desktop'); // 데스크톱에서만 LiveSection 로딩
  const [listLoading, setListLoading] = useState(true);

  // 전체 로딩 상태 (3개 섹션 중 하나라도 로딩 중이면 true)
  const isAnyLoading = recommendationLoading || liveLoading || listLoading;

  // 섹션별 로딩 상태 콜백 함수들
  const handleRecommendationLoadingChange = useCallback((loading: boolean) => {
    setRecommendationLoading(loading);
  }, []);

  const handleLiveLoadingChange = useCallback((loading: boolean) => {
    setLiveLoading(loading);
  }, []);

  const handleListLoadingChange = useCallback((loading: boolean) => {
    setListLoading(loading);
  }, []);

  // 로그인 모달 핸들러
  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  // 좋아요 클릭 핸들러
  const handleLikeClick = async (e: React.MouseEvent, lotId: number, currentLikedUserIndexes?: number[]) => {
    e.preventDefault();
    e.stopPropagation();

    // 로그인하지 않은 경우 로그인 모달 표시
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    try {
      await favoriteManager.handleLikeClick(lotId, user.index, currentLikedUserIndexes);
    } catch (error) {
      devError('좋아요 처리 실패:', error);
      alert('좋아요 처리에 실패했습니다.');
    }
  };

  return (
    <MainLayout currentPage="home" paddingTop={20}>
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          showLogo={true}
          showMobileSearch={true}
        />
        <HomeContainer>
          <ContentContainer>
            {/* 로딩 중일 때 DataLoader 표시 */}
            {isAnyLoading && (
              <LoaderContainer>
                <DataLoader/>
              </LoaderContainer>
            )}
            
            {/* 로딩이 완료되면 섹션들 표시 */}
            <div style={{ display: isAnyLoading ? 'none' : 'block' }}>
              {/* 모바일에서만 표시되는 빈 공간 */}
              <MobileSpacer />
              <RecommendationSection 
                onLikeClick={handleLikeClick}
                favoriteManager={favoriteManager}
                onLoadingChange={handleRecommendationLoadingChange}
              />
              {/* 모바일에서는 바텀 네비게이션으로 진행 경매에 접근할 수 있으므로 LiveSection 숨김 */}
              {device === 'desktop' && (
                <LiveSection 
                  onLoadingChange={handleLiveLoadingChange}
                />
              )}
              <ListSection 
                onLikeClick={handleLikeClick}
                favoriteManager={favoriteManager}
                onLoadingChange={handleListLoadingChange}
                showPriceInfo={true}
              />
              <Footer />
            </div>
          </ContentContainer>
        </HomeContainer>
        
        {/* 로그인 필요 모달 */}
        <LoginRequiredModal
          isOpen={showLoginModal}
          onClose={handleCloseLoginModal}
          onLogin={handleLoginFromModal}
        />
      </CenteredLayout>
    </MainLayout>
  );
}

export default Home;
